import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Button, Form } from "antd";
import axios from "axios";

// Form Elements
import InputText from "@Vytelle/admin/src/components/Common/FormElements/Text";
import Email from "@Vytelle/admin/src/components/Common/FormElements/Email";
import PhoneNumber from "@Vytelle/admin/src/components/Common/FormElements/PhoneNumber";
import TimezoneInput from "@Vytelle/admin/src/components/Common/FormElements/TimeZoneDropdown";
import Password from "@Vytelle/admin/src/components/Common/FormElements/Password";
import Dropdown from "@Vytelle/admin/src/components/Common/FormElements/Dropdown";

import Loading from "@Vytelle/admin/src/components/Loading";

// Constants
import LOCALIZATION from "@Vytelle/admin/src/services/LocalizationService";

import { useDispatch } from "react-redux";

import { postAction } from "store/actions/CRUDAction";
import { API_URLS_COMMON } from "constants/ApiUrlCommon";
import { errorNotification } from "helpers/Notification";

import "./style.scss";
import TermsCheckbox from "./components/TermsCheckbox";

function CreateUserSignUpForm({ onSuccess, onError }) {
  const REASON_OPTIONS = [
    { name: LOCALIZATION.USER_SIGN_UP.REASON.EMBRYO_RESULTS, value: "EmbryoResults" },
    { name: LOCALIZATION.USER_SIGN_UP.REASON.VYTELLE_LINK, value: "VytelleLink" },
  ];
  
  const validateOfferCode = async (rule, value) => {
    if (!value || value.trim() === "") {
      return Promise.resolve();
    }
    
    try {
      const response = await dispatch(postAction(API_URLS_COMMON.VALIDATE_OFFER_CODE, { offerCode: value }));
      
      if (response?.data?.isValid === false) {
        return Promise.reject(new Error("The offer code provided is invalid."));
      }
      
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(new Error("Unable to validate offer code. Please try again."));
    }
  };
  
  const handleCancel = () => {
    window.location.href = LOCALIZATION.VYTELE_HOME_PAGE_URL;
  };
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = React.useState(false);

  const onSubmit = (values) => {
    setIsLoading(true);
    const { password, confirmPassword, agreeToTerms } = values || {};

    if (password !== confirmPassword) {
      errorNotification(LOCALIZATION.NEW_CONFIRM_PASSWORD_NOT_SAME);
      setIsLoading(false);
      return;
    }

    if (!agreeToTerms) {
      errorNotification(LOCALIZATION.USER_SIGN_UP.TERMS_AND_CONDITIONS.VALIDATION_MESSAGE);
      setIsLoading(false);
      return;
    }

    const submitData = { ...values };
    delete submitData.agreeToTerms;
    delete submitData.confirmPassword;

    dispatch(postAction(API_URLS_COMMON.SIGNUP_USER, submitData)).then(
      () => {
        onSuccess();
        form.resetFields();
        setIsLoading(false);
      },
      (error) => {
        if (error?.response?.status === 400 && error?.response?.data) {
          const errorData = error.response.data;
          const field = errorData?.field || errorData?.Field;
          const message = errorData?.message || errorData?.Message;
          
          if ((field === "OfferCode" || field === "offerCode") && message) {
            form.setFields([
              {
                name: "offerCode",
                errors: [message],
              },
            ]);
            setIsLoading(false);
            return;
          }
        }
        onError();
        setIsLoading(false);
      }
    );
  };

  return (
    <>
      <section id="create-user-signup">
        <h1 className="title">{LOCALIZATION.USER_SIGN_UP.TITLE}</h1>
      </section>
      <hr className="signup-separator" />
      <Form form={form} layout="vertical" onFinish={onSubmit} className="signup-form">
        {isLoading && <Loading />}
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12}>
            <InputText
              name={"firstName"}
              data-testid="first-name"
              label={LOCALIZATION.FIRST_NAME}
              required
              maxLength={32}
              placeholder={LOCALIZATION.ENTER_FIRST_NAME}
            />
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12}>
            <InputText
              name={"lastName"}
              data-testid="last-name"
              label={LOCALIZATION.LAST_NAME}
              required
              maxLength={32}
              placeholder={LOCALIZATION.ENTER_LAST_NAME}
            />
          </Col>
        </Row>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={24} lg={12} xl={12}>
            <Email
              name={"email"}
              data-testid="email"
              label={LOCALIZATION.EMAIL_ADDRESS}
              required
              placeholder={LOCALIZATION.ENTER_EMAIL}
            />
          </Col>
          <Col xs={24} sm={12} md={24} lg={12} xl={12}>
            <PhoneNumber
              name={"primaryPhone"}
              data-testid="primary-phone"
              label={LOCALIZATION.PHONE}
              required
              placeholder={LOCALIZATION.PHONE_PLACEHOLDER}
            />
          </Col>
        </Row>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={24} lg={12} xl={12}>
            <InputText
              name={"companyName"}
              data-testid="company-name"
              label={LOCALIZATION.COMPANY}
              required
              maxLength={64}
              placeholder="Enter company name"
            />
          </Col>
          <Col xs={24} sm={12} md={24} lg={12} xl={12}>
            <TimezoneInput
              name={"timeZone"}
              data-testid="time-zone"
              label={LOCALIZATION.TIME_ZONE}
              required
              showSearch
              placeholder={LOCALIZATION.SELECT_TIME_ZONE}
            />
          </Col>
        </Row>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={24} lg={12} xl={12}>
            <Password
              className="form-control"
              name="password"
              data-testid="password"
              label={LOCALIZATION.PASSWORD}
              required={true}
              minLength={1}
              placeholder="Enter password"
              validator={[
                { min: 8, message: LOCALIZATION.PASSWORD_MUST_HAVE_8_CHARACTERS },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={24} lg={12} xl={12}>
            <Password
              className="form-control"
              name="confirmPassword"
              data-testid="confirm-password"
              label={LOCALIZATION.CONFIRM_PASSWORD}
              required={true}
              minLength={1}
              placeholder={LOCALIZATION.CONFIRM_PASSWORD}
            />
          </Col>
        </Row>
        <Row gutter={[24, 16]}>
          <Col xs={24}>
            <InputText
              name={"offerCode"}
              data-testid="offer-code"
              label={LOCALIZATION.USER_SIGN_UP.OFFER_CODE}
              required={false}
              maxLength={50}
              placeholder="Enter offer code"
              validator={{ validator: validateOfferCode }}
              hasFeedback
            />
          </Col>
        </Row>
        <Row gutter={[24, 16]}>
          <Col xs={24}>
            <Dropdown
              name={"signUpReasons"}
              data-testid="sign-up-reasons"
              label="Reason for Sign Up"
              required
              mode="multiple"
              options={REASON_OPTIONS}
              placeholder="Select reason(s)"
            />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24}>
            <TermsCheckbox
              name="agreeToTerms"
              data-testid="terms-checkbox"
            />
          </Col>
        </Row>
        <Row
          className="d-flex justify-end mt-auto form-actions"
          gutter={[16, { xs: 8, xl: 0 }]}
        >
          <Col>
            <Button size="large" onClick={handleCancel} data-testid="btn-cancel">
              {LOCALIZATION.CANCEL}
            </Button>
          </Col>
          <Col>
            <Button type="primary" size="large" htmlType="submit" data-testid="btn-submit">
              {LOCALIZATION.CREATE_USER_SIGNUP}
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default CreateUserSignUpForm;
